import React from 'react';
import { User, Mail, Key, Bell, Shield, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';

const ProfilePage = () => {
  // Mock data (placeholder)
  const user = {
    name: 'John Doe',
    email: 'john.doe@company.com',
    avatar: null,
    plan: 'Professional',
    joinedDate: 'January 2025',
  };

  const settingsSections = [
    {
      title: 'Account',
      items: [
        { icon: User, label: 'Edit Profile', description: 'Update your name and avatar' },
        { icon: Mail, label: 'Email Settings', description: 'Manage email preferences' },
        { icon: Key, label: 'Change Password', description: 'Update your password' },
      ]
    },
    {
      title: 'Preferences',
      items: [
        { icon: Bell, label: 'Notifications', description: 'Configure notification settings' },
        { icon: Shield, label: 'Privacy & Security', description: 'Manage security options' },
      ]
    }
  ];

  return (
    <div className="h-full bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800/50">
        <h1 className="text-xl font-semibold text-white">Profile & Settings</h1>
        <p className="text-sm text-gray-400">Manage your account and preferences</p>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-2xl space-y-6">
          {/* Profile Card */}
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  {user.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-white text-lg font-semibold">{user.name}</h2>
                <p className="text-gray-400 text-sm">{user.email}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">
                    {user.plan}
                  </span>
                  <span className="text-xs text-gray-500">
                    Member since {user.joinedDate}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Sections */}
          {settingsSections.map((section) => (
            <div key={section.title} className="bg-slate-800/40 border border-slate-700/30 rounded-xl">
              <div className="px-5 py-4 border-b border-slate-700/30">
                <h3 className="text-white font-medium">{section.title}</h3>
              </div>
              <div className="divide-y divide-slate-700/30">
                {section.items.map((item) => (
                  <button
                    key={item.label}
                    className="w-full px-5 py-4 flex items-center gap-4 hover:bg-slate-800/50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{item.label}</p>
                      <p className="text-gray-500 text-xs">{item.description}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Logout */}
          <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl">
            <button className="w-full px-5 py-4 flex items-center gap-4 hover:bg-red-500/10 transition-colors text-left rounded-xl">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <LogOut className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-red-400 text-sm font-medium">Log Out</p>
                <p className="text-gray-500 text-xs">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
